import React, { Component } from "react";
import Slider from "react-slick";
import axios from "axios";
import ProductCard from "./ProductCard";

import "slick-carousel/slick/slick.css";
import "slick-carousel/slick/slick-theme.css";
import "./ProductSlider.css";

// Custom Arrow Components
const CustomPrevArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <div
      className="custom-arrow custom-prev-arrow"
      style={{ ...style }}
      onClick={onClick}
    >
      <i className="fas fa-chevron-left"></i>
    </div>
  );
};

const CustomNextArrow = (props) => {
  const { className, style, onClick } = props;
  return (
    <div
      className="custom-arrow custom-next-arrow"
      style={{ ...style }}
      onClick={onClick}
    >
      <i className="fas fa-chevron-right"></i>
    </div>
  );
};

class ProductSlider extends Component {
  constructor() {
    super();
    this.state = {
      slides: [],
      loading: true,
      error: "",
      currentSlide: 0
    };
  }

  componentDidMount() {
    this.fetchSlides();
  }

  fetchSlides = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/getServiceMain`);
      const data = response.data;

      if (data.status === "ok") {
        this.setState({ slides: data.data, loading: false });
      } else {
        this.setState({ error: data.message, loading: false });
      }
    } catch (error) {
      this.setState({ 
        error: "Unable to load services. Please try again later.", 
        loading: false 
      });
    }
  };

  handleSlideChange = (index) => {
    this.setState({ currentSlide: index });
  };

  render() {
    const { slides, loading, error, currentSlide } = this.state;

    const settings = {
      dots: true,
      infinite: slides.length > 5,
      speed: 500,
      slidesToShow: 5,
      slidesToScroll: 1,
      autoplay: true,
      autoplaySpeed: 3000,
      pauseOnHover: true,
      prevArrow: <CustomPrevArrow />,
      nextArrow: <CustomNextArrow />,
      beforeChange: (current, next) => this.handleSlideChange(next),
      customPaging: (i) => (
        <div className="custom-dot">
          <span></span>
        </div>
      ),
      responsive: [
        {
          breakpoint: 1200,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
            dots: true
          }
        },
        {
          breakpoint: 992,
          settings: {
            slidesToShow: 3,
            slidesToScroll: 1,
            dots: true
          }
        },
        {
          breakpoint: 768,
          settings: {
            slidesToShow: 2,
            slidesToScroll: 1,
            dots: true,
            arrows: false
          }
        },
        {
          breakpoint: 576,
          settings: {
            slidesToShow: 1,
            slidesToScroll: 1,
            dots: true,
            arrows: false,
            centerMode: true,
            centerPadding: "20px"
          }
        }
      ]
    };

    return (
      <div className="professional-slider-container">
        {/* Section Header */}
        <div className="slider-header">
          <div className="header-content">
            <h2 className="section-title">
              <i className="fas fa-star-of-life title-icon"></i>
              Our Services
            </h2>
            <p className="section-subtitle">
              Discover our wide range of professional services
            </p>
          </div>
          <div className="view-all-container">
            <button className="view-all-btn">
              View All Services
              <i className="fas fa-arrow-right"></i>
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="loading-container">
            <div className="loading-spinner">
              <div className="spinner"></div>
              <p>Loading services...</p>
            </div>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="error-container">
            <div className="error-content">
              <i className="fas fa-exclamation-triangle error-icon"></i>
              <h3>Oops! Something went wrong</h3>
              <p>{error}</p>
              <button className="retry-btn" onClick={this.fetchSlides}>
                <i className="fas fa-redo"></i>
                Try Again
              </button>
            </div>
          </div>
        )}

        {/* Services Slider */}
        {!loading && !error && slides.length > 0 && (
          <div className="slider-wrapper">
            <div className="slider-counter">
              <span className="current-slide">{currentSlide + 1}</span>
              <span className="separator">/</span>
              <span className="total-slides">{slides.length}</span>
            </div>
            
            <Slider {...settings} className="professional-service-slider">
              {slides.map((slide, index) => (
                <div key={slide._id || index} className="slide-item">
                  <ProfessionalProductCard 
                    _id={slide._id}  
                    imgSrc={`${process.env.REACT_APP_API_URL}/${slide.image.replace('\\', '/')}`} 
                    name={slide.name} 
                    description={slide.description}
                    index={index}
                  />
                </div>
              ))}
            </Slider>
          </div>
        )}

        {/* Empty State */}
        {!loading && !error && slides.length === 0 && (
          <div className="empty-state">
            <div className="empty-content">
              <i className="fas fa-search empty-icon"></i>
              <h3>No Services Available</h3>
              <p>We're working to add more services. Please check back later.</p>
            </div>
          </div>
        )}
      </div>
    );
  }
}

// Professional Product Card Component
const ProfessionalProductCard = ({ _id, imgSrc, name, description, index }) => {
  const handleCardClick = () => {
    // Navigate to service details
    window.location.href = `/ServiceView/${_id}`;
  };

  const handleEnquiryClick = (e) => {
    e.stopPropagation();
    // Handle enquiry logic
    console.log(`Enquiry for service: ${name}`);
  };

  return (
    <div className="professional-card" onClick={handleCardClick}>
      {/* Card Badge */}
      {index < 3 && (
        <div className="card-badge">
          {index === 0 && <span className="badge-popular">Popular</span>}
          {index === 1 && <span className="badge-trending">Trending</span>}
          {index === 2 && <span className="badge-featured">Featured</span>}
        </div>
      )}

      {/* Service Image */}
      <div className="card-image-container">
        <img 
          src={imgSrc} 
          alt={name}
          className="card-image"
          loading="lazy"
          onError={(e) => {
            e.target.src = '/images/default-service.png';
          }}
        />
        <div className="image-overlay">
          <div className="overlay-content">
            <i className="fas fa-eye view-icon"></i>
            <span>View Details</span>
          </div>
        </div>
      </div>

      {/* Card Content */}
      <div className="card-content">
        <h3 className="service-name">{name}</h3>
        <p className="service-description">{description}</p>
        
        {/* Service Features */}
        <div className="service-features">
          <div className="feature-item">
            <i className="fas fa-check-circle"></i>
            <span>Verified Service</span>
          </div>
          <div className="feature-item">
            <i className="fas fa-clock"></i>
            <span>Quick Response</span>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="card-actions">
          <button className="enquiry-button" onClick={handleEnquiryClick}>
            <i className="fas fa-envelope"></i>
            Get Quote
          </button>
          <button className="call-button">
            <i className="fas fa-phone"></i>
            Call Now
          </button>
        </div>

        {/* Rating */}
        <div className="service-rating">
          <div className="stars">
            {[1, 2, 3, 4, 5].map((star) => (
              <i 
                key={star} 
                className={`fas fa-star ${star <= 4 ? 'filled' : ''}`}
              ></i>
            ))}
          </div>
          <span className="rating-text">4.0 (120+ reviews)</span>
        </div>
      </div>
    </div>
  );
};

export default ProductSlider;